import React from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/create');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Можно добавить уведомление о копировании
  };

  return (
    <div className="min-h-screen">
      {/* Pump.fun Launch Banner */}
      <div className="pump-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pump-banner-content">
            <div className="pump-text">
              <span className="pump-label">We Are Launched on</span>
              <a 
                href="https://pump.fun/coin/t978dyZQ1Fe7tSphBneqhy9m8BB65t2dwqq5qompump" 
                target="_blank" 
                rel="noopener noreferrer"
                className="pump-link"
              >
                pump.fun
              </a>
            </div>
            <div className="pump-address">
              <span className="address-text">t978dyZQ1Fe7tSphBneqhy9m8BB65t2dwqq5qompump</span>
              <button 
                onClick={() => copyToClipboard('t978dyZQ1Fe7tSphBneqhy9m8BB65t2dwqq5qompump')}
                className="copy-btn"
                title="Copy address"
              >
                📋
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hero + Features Section with unified animated background */}
      <div className="hero-features-container">
        {/* Single Animated Waves Background */}
        <div className="waves-container">
          <div className="wave wave-1"></div>
          <div className="wave wave-2"></div>
          <div className="wave wave-3"></div>
          <div className="wave wave-4"></div>
        </div>
        
        {/* Hero Section */}
        <section className="hero-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="fade-in-up">
            <h1 className="hero-title">
              Welcome to{' '}
              <span className="streamlab-text">Stream<span className="lab">Lab</span></span>
            </h1>
              <p className="hero-subtitle">
                The ultimate platform for streamers to receive SOL donations with real-time notifications and beautiful OBS integration
              </p>
              <div className="hero-actions">
                <button
                  onClick={handleGetStarted}
                  className="btn-primary btn-lg"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              Why Choose Us?
            </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Built specifically for the Solana ecosystem with streamers in mind
              </p>
            </div>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">⚡</div>
                <h3 className="feature-title">Lightning Fast</h3>
                <p className="feature-description">
                  Real-time SOL transactions with instant confirmation and immediate OBS notifications
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🔒</div>
                <h3 className="feature-title">Secure & Decentralized</h3>
                <p className="feature-description">
                  Built on Solana blockchain with Phantom wallet integration for maximum security
                </p>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🎮</div>
                <h3 className="feature-title">OBS Ready</h3>
                <p className="feature-description">
                  Beautiful real-time donation display that integrates seamlessly with OBS Studio
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Get started in minutes with our simple 3-step process
            </p>
          </div>

          <div className="steps-grid">
            <div className="step-item">
              <div className="step-number">1</div>
              <h3 className="step-title">Connect Wallet</h3>
              <p className="step-description">
                Connect your Phantom wallet to create and manage your donation pages
              </p>
            </div>

            <div className="step-item">
              <div className="step-number">2</div>
              <h3 className="step-title">Create Page</h3>
              <p className="step-description">
                Set your minimum donation amount and customize your page description
              </p>
            </div>

            <div className="step-item">
              <div className="step-number">3</div>
              <h3 className="step-title">Start Receiving</h3>
              <p className="step-description">
                Share your donation page and watch donations appear in real-time on OBS
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">0.001</div>
              <div className="stat-label">Min SOL Donation</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">~1s</div>
              <div className="stat-label">Transaction Time</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">100%</div>
              <div className="stat-label">Decentralized</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">Free</div>
              <div className="stat-label">To Use</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="cta-card">
            <h2 className="cta-title">
              Ready to Start Earning?
            </h2>
            <p className="cta-subtitle">
              Join the future of streaming donations with Solana blockchain technology
            </p>
            <div className="text-center">
              <button
                onClick={handleGetStarted}
                className="btn-primary btn-lg"
              >
                Create Your First Page
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="footer-brand">
            <span className="streamlab-text">Stream<span className="lab">Lab</span></span>
          </h3>
          <p className="footer-description">
            Built for streamers, powered by Solana
          </p>
          <div className="footer-links">
            <a href="/dashboard" className="footer-link">Dashboard</a>
            <a href="/create" className="footer-link">Create Page</a>
            <a href="/how-it-works" className="footer-link">How It Works</a>
            <a href="https://x.com/streamlabfun" target="_blank" rel="noopener noreferrer" className="footer-link x-link">
              <span className="x-icon">𝕏</span>
            </a>
          </div>
          <div className="footer-copyright">
            © 2025 StreamLab • Powered by Solana • Made with ❤️ for Streamers
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;